import { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Form, Input, Select, Button, Space, Spin, Alert, message, DatePicker } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getCampaign,
  createCampaign,
  updateCampaign,
  type CreateCampaignDto,
  type UpdateCampaignDto,
} from '@/api/marketingApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const TYPE_OPTIONS = ['email', 'sms', 'social'];
const STATUS_OPTIONS = ['draft', 'scheduled', 'active', 'paused', 'completed', 'cancelled'];

interface CampaignFormValues {
  name: string;
  description?: string;
  type: string;
  status?: string;
  owner_id?: string;
  tags?: string[];
  scheduled_at?: dayjs.Dayjs;
  filter_industry?: string;
  filter_source?: string;
  filter_status?: string;
  filter_tags?: string[];
}

export default function CampaignForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<CampaignFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getCampaign(id)
      .then((campaign) => {
        form.setFieldsValue({
          name: campaign.name,
          description: campaign.description || undefined,
          type: campaign.type || 'email',
          status: campaign.status || 'draft',
          owner_id: campaign.owner_id || undefined,
          tags: (campaign.tags?.length ?? 0) > 0 ? campaign.tags : undefined,
          scheduled_at: campaign.schedule?.scheduled_at
            ? dayjs(campaign.schedule.scheduled_at)
            : undefined,
          filter_industry: campaign.target_audience?.filter_industry || undefined,
          filter_source: campaign.target_audience?.filter_source || undefined,
          filter_status: campaign.target_audience?.filter_status || undefined,
          filter_tags:
            (campaign.target_audience?.filter_tags?.length ?? 0) > 0
              ? campaign.target_audience.filter_tags!
              : undefined,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: CampaignFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const audience = {
        filter_industry: values.filter_industry || undefined,
        filter_source: values.filter_source || undefined,
        filter_status: values.filter_status || undefined,
        filter_tags: values.filter_tags || undefined,
      };

      if (isEdit && id) {
        const data: UpdateCampaignDto = {
          name: values.name,
          description: values.description || undefined,
          type: values.type,
          status: values.status || undefined,
          target_audience: audience,
          scheduled_at: values.scheduled_at?.toISOString() || undefined,
          owner_id: values.owner_id || undefined,
          tags: values.tags || undefined,
        };
        await updateCampaign(id, data);
        message.success(t('campaigns.update_success'));
        navigate(`/marketing/campaigns/${id}`);
      } else {
        const data: CreateCampaignDto = {
          name: values.name,
          description: values.description || undefined,
          type: values.type,
          target_audience: audience,
          scheduled_at: values.scheduled_at?.toISOString() || undefined,
          owner_id: values.owner_id || undefined,
          tags: values.tags || undefined,
        };
        const created = await createCampaign(data);
        message.success(t('campaigns.create_success'));
        navigate(`/marketing/campaigns/${created.id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('app.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('campaigns', 'write')) {
    return <Navigate to="/marketing" replace />;
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(isEdit ? `/marketing/campaigns/${id}` : '/marketing')}
        >
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('campaigns.edit_title') : t('campaigns.create_title')}</h2>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ type: 'email', status: 'draft' }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="name"
          label={t('campaigns.field.name')}
          rules={[
            { required: true, message: t('campaigns.validation.name_required') },
            { max: 500, message: t('campaigns.validation.name_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('campaigns.field.description')}
          rules={[{ max: 2000, message: t('campaigns.validation.description_max_length') }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('campaigns.field.type')}
          rules={[{ required: true, message: t('campaigns.validation.type_required') }]}
        >
          <Select>
            {TYPE_OPTIONS.map((opt) => (
              <Select.Option key={opt} value={opt}>
                {t(`campaigns.type_options.${opt}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('campaigns.field.status')}>
            <Select>
              {STATUS_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>
                  {t(`campaigns.status_options.${opt}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="owner_id" label={t('campaigns.field.owner_id')}>
          <Input />
        </Form.Item>

        <Form.Item name="scheduled_at" label={t('campaigns.field.scheduled_at')}>
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="tags" label={t('campaigns.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        {/* Target Audience Filters */}
        <h3>{t('campaigns.audience.title')}</h3>

        <Form.Item name="filter_industry" label={t('campaigns.audience.filter_industry')}>
          <Input />
        </Form.Item>

        <Form.Item name="filter_source" label={t('campaigns.audience.filter_source')}>
          <Input />
        </Form.Item>

        <Form.Item name="filter_status" label={t('campaigns.audience.filter_status')}>
          <Input />
        </Form.Item>

        <Form.Item name="filter_tags" label={t('campaigns.audience.filter_tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button
              onClick={() => navigate(isEdit ? `/marketing/campaigns/${id}` : '/marketing')}
            >
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
